// src/pages/pet/ReportPetLost.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker } from '@react-google-maps/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import '../../style/ReportPetLost.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 }; // fallback
const MAP_LIBRARIES = ['places', 'maps'];

const ReportPetLost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // Step 1 or Step 2
  const [lastSeenDate, setLastSeenDate] = useState('');
  const [lastSeenTime, setLastSeenTime] = useState('');
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null); // { lat, lng }
  const [description, setDescription] = useState('');
  const [lostFiles, setLostFiles] = useState([]); // File[]
  const [imagePreviews, setImagePreviews] = useState([]); // State for image previews
  const [contactMethods, setContactMethods] = useState({ email: true, whatsapp: false });
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(true);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoadingUserData(true);
        const storedUser = JSON.parse(localStorage.getItem('user'));

        if (!storedUser || !storedUser._id || !storedUser.token) {
          console.error('Invalid or missing user data in localStorage:', storedUser);
          if (!storedUser) {
            console.error('No user data found in localStorage.');
          } else if (!storedUser._id) {
            console.error('User ID (_id) is missing in storedUser:', storedUser);
          } else if (!storedUser.token) {
            console.error('Token is missing in storedUser:', storedUser);
          }
          setError(t('reportPetLostPage.error.missingUserData'));
          setLoadingUserData(false);
          navigate('/login'); // Redirect to login if user data is invalid
          return;
        }

        const token = storedUser.token;
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/account/${storedUser._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const user = response.data;
        setEmail(user.email);
        setPhoneNumber(user.phoneNumber || '');
        setError(null); // Clear any previous errors
      } catch (error) {
        console.error('Failed to fetch user details:', error);
        setError(t('reportPetLostPage.error.fetchUserDetails'));
      } finally {
        setLoadingUserData(false);
      }
    };

    fetchUserDetails();
  }, [t, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!coordinates) {
      setError(t('reportPetLostPage.error.invalidLocation'));
      setSubmitting(false);
      return;
    }

    let lastSeenDateTime = null;
    if (lastSeenDate && lastSeenTime) {
      lastSeenDateTime = new Date(`${lastSeenDate}T${lastSeenTime}`);
    }

    const formattedLastSeenLocation = {
      type: 'Point',
      coordinates: [coordinates.lng, coordinates.lat],
    };

    const reportData = {
      lastSeenLocation: formattedLastSeenLocation,
      lastSeenDate: lastSeenDateTime,
      description,
      images: lostFiles,
      contactInfo: {
        email: contactMethods.email ? email : null,
        phoneNumber: contactMethods.whatsapp ? phoneNumber : phoneNumber, // Always send the latest phoneNumber
      },
    };

    console.log('Contact Info being sent:', reportData.contactInfo);

    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const token = storedUser?.token || localStorage.getItem('token');

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/account/${storedUser._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const user = res.data;

      // Always update contact info regardless of existing data
      await handleUpdateContactInfo();

      const formData = new FormData();
      formData.append('lastSeenLocation', JSON.stringify(reportData.lastSeenLocation));
      formData.append('lastSeenDate', reportData.lastSeenDate);
      formData.append('description', reportData.description);
      lostFiles.forEach((file) => formData.append('images', file));
      formData.append('contactInfo', JSON.stringify(reportData.contactInfo));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/pets/${id}/report-lost`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      console.log('Backend response:', response);

      if (!response.ok) {
        throw new Error(t('reportPetLostPage.error.reportFailed'));
      }

      const data = await response.json();
      navigate(`/lost-pet/${id}`);
    } catch (err) {
      console.error(t('reportPetLostPage.error.reportError'), err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateContactInfo = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const token = storedUser?.token || localStorage.getItem('token');

      if (!token) {
        throw new Error(t('reportPetLostPage.error.noAuthToken'));
      }
      const payload = { phoneNumber };

      console.log('Payload being sent to update contact info:', { phoneNumber });

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/account/${storedUser._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Backend response for contact info update:', response);
    } catch (error) {
      console.error(t('reportPetLostPage.error.updateContactInfo'), error.response?.data || error.message);
    }
  };

  const handleNextStep = () => {
    if (loadingUserData) {
      setError(t('reportPetLostPage.error.loadingUserData')); // Prevent navigation if user data is still loading
      return;
    }
    if (!email) {
      setError(t('reportPetLostPage.error.missingUserData')); // Prevent navigation if email is missing
      return;
    }
    setError(null); // Clear any previous errors
    setStep(2);
  };

  // Ensure error is cleared when retrying data fetch
  useEffect(() => {
    if (!loadingUserData && email && phoneNumber ) {
      setError(null);
    }
  }, [loadingUserData, email, phoneNumber]);

  // Add logs to verify state updates

  useEffect(() => {
  }, [phoneNumber]);

  if (loadError) return <div>{t('reportPetLostPage.error.mapLoadFailed')}</div>;
  if (!isLoaded) return <div>{t('reportPetLostPage.loadingMap')}</div>;
  if (loadingUserData) {
    return <div>{t('reportPetLostPage.loadingUserData')}</div>;
  }

  return (
    <div className="report-pet-lost-page">
      <h1>{t('reportPetLostPage.title')}</h1>
      {error && <p style={{ color: 'red' }}>{t('reportPetLostPage.error.generalError', { error })}</p>}
      {submitting ? (
        <p style={{ color: 'green' }}>{t('reportPetLostPage.submitting')}</p>
      ) : (
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div>
              <div>
                <label>{t('reportPetLostPage.dateLost')}</label>
                <input type="date" value={lastSeenDate} onChange={(e) => setLastSeenDate(e.target.value)} required />
              </div>

              <div>
                <label>{t('reportPetLostPage.timeLost')}</label>
                <input type="time" value={lastSeenTime} onChange={(e) => setLastSeenTime(e.target.value)} required />
              </div>

              <div>
                <label>{t('reportPetLostPage.address')}</label>
                <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={() => {
                  const ac = autocompleteRef.current;
                  if (!ac) return;
                  const place = ac.getPlace();
                  if (place.geometry && place.geometry.location) {
                    setCoordinates({
                      lat: place.geometry.location.lat(),
                      lng: place.geometry.location.lng(),
                    });
                    setAddress(place.formatted_address);
                  }
                }}>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('reportPetLostPage.addressPlaceholder')}
                  />
                </Autocomplete>
              </div>

              <div className="map-preview">
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : DEFAULT_CENTER}
                  zoom={coordinates ? 15 : 12}
                  onLoad={(map) => (mapRef.current = map)}
                >
                  {coordinates && <Marker position={{ lat: coordinates.lat, lng: coordinates.lng }} />}
                </GoogleMap>
              </div>

              <div>
                <label>{t('reportPetLostPage.additionalPictures')}</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setLostFiles(files);
                    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
                  }}
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                  {imagePreviews.map((src, index) => (
                    <img
                      key={index}
                      src={src}
                      alt={t('reportPetLostPage.previewAlt', { index: index + 1 })}
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label>{t('reportPetLostPage.description')}</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
              </div>

              <div style={{ marginTop: 16 }}>
                <button type="button" onClick={handleNextStep}>{t('reportPetLostPage.next')}</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3>{t('reportPetLostPage.contactInfo')}</h3>
              <div>
                <label>{t('reportPetLostPage.email')}</label>
                <p>{email}</p>
              </div>
              <div>
                <label>{t('reportPetLostPage.phoneNumber')}</label>
                <PhoneInput
                  country={'us'}
                  value={phoneNumber}
                  onChange={(value) => {
                    setPhoneNumber(value);
                    console.log('Updated phoneNumber:', value);
                  }}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    autoFocus: false,
                  }}
                  enableSearch
                  placeholder={t('reportPetLostPage.phoneNumberPlaceholder')}
                  containerClass="w-full"
                  inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                  buttonClass="border border-gray-300 rounded-l-md"
                  dropdownClass="react-phone-dropdown"
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <button type="button" onClick={() => setStep(1)}>{t('reportPetLostPage.back')}</button>
                <button type="submit">{t('reportPetLostPage.submit')}</button>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default ReportPetLost;
